import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Sun, 
  Lightbulb, 
  TrendingDown, 
  Wrench, 
  Home, 
  Building2,
  CheckCircle2,
  ArrowRight 
} from 'lucide-react';

const Servicios = () => {
  const mainServices = [
    {
      icon: Sun,
      title: 'Instalación de Paneles Solares',
      description: 'Diseño e instalación de sistemas fotovoltaicos de alta eficiencia adaptados a tus necesidades energéticas. Utilizamos los mejores materiales del mercado con garantía de fabricante de 25 años.',
      features: [
        'Paneles solares de última generación',
        'Inversores de alta eficiencia',
        'Sistemas de montaje premium',
        'Monitorización en tiempo real',
        'Garantía total de 10 años'
      ],
      image: 'https://images.pexels.com/photos/30440447/pexels-photo-30440447.jpeg'
    },
    {
      icon: Lightbulb,
      title: 'Asesoramiento Energético',
      description: 'Análisis completo de tu consumo energético actual y diseño de la solución solar óptima para maximizar tu ahorro. Te ayudamos a entender tu perfil de consumo y a tomar la mejor decisión.',
      features: [
        'Estudio personalizado de consumo',
        'Análisis de orientación y espacio',
        'Cálculo preciso de ahorro',
        'Opciones de financiación',
        'Tramitación de subvenciones'
      ],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f'
    },
    {
      icon: TrendingDown,
      title: 'Optimización del Consumo',
      description: 'Implementamos soluciones inteligentes para gestionar y optimizar tu consumo eléctrico, maximizando el aprovechamiento de la energía solar generada y minimizando los vertidos a la red.',
      features: [
        'Sistemas de gestión inteligente',
        'Optimización de autoconsumo',
        'Programación de cargas',
        'Análisis de eficiencia',
        'Recomendaciones personalizadas'
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
    },
    {
      icon: Wrench,
      title: 'Mantenimiento y Soporte',
      description: 'Servicio técnico especializado para mantener tu instalación solar en óptimas condiciones. Realizamos revisiones periódicas y atendemos cualquier incidencia con rapidez y profesionalidad.',
      features: [
        'Revisiones periódicas',
        'Limpieza de paneles',
        'Monitorización remota',
        'Asistencia técnica 24/7',
        'Repuestos originales'
      ],
      image: 'https://images.pexels.com/photos/8853504/pexels-photo-8853504.jpeg'
    }
  ];

  const targetServices = [
    {
      icon: Home,
      title: 'Soluciones para Viviendas',
      description: 'Sistemas fotovoltaicos diseñados específicamente para hogares, desde viviendas unifamiliares hasta comunidades de vecinos.',
      benefits: [
        'Reducción de factura del 70%',
        'Amortización en 1-3 años',
        'Aumento del valor inmobiliario',
        'Financiación sin entrada',
        'Ayudas y subvenciones disponibles'
      ]
    },
    {
      icon: Building2,
      title: 'Soluciones para Negocios',
      description: 'Instalaciones a gran escala para industrias, oficinas, hoteles y centros comerciales que buscan reducir costes operativos.',
      benefits: [
        'Reducción de costes fijos',
        'ROI en menos de 3 años',
        'Mejora de imagen corporativa',
        'Ventajas fiscales',
        'Soluciones escalables'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Servicios de Energía Solar - Madrid, Valencia y Extremadura | Nubia Solar</title>
        <meta name="description" content="Instalación de placas solares, asesoramiento energético y mantenimiento en Madrid, Valencia y Extremadura. Soluciones para hogares y empresas. ☎️ 604 92 17 45" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Nuestros Servicios
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Soluciones integrales de energía solar adaptadas a cada necesidad
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-24">
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${!isEven ? 'md:flex-row-reverse' : ''}`}>
                  <div className={isEven ? 'md:order-1' : 'md:order-2'}>
                    <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-nubia-gold-primary" />
                    </div>
                    <h2 className="text-4xl font-bold text-nubia-green-primary mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-nubia-text-dark/70 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-nubia-green-support mt-0.5 flex-shrink-0" />
                          <span className="text-nubia-text-dark/70">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={isEven ? 'md:order-2' : 'md:order-1'}>
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="rounded-2xl nubia-shadow-lg w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Target Services */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Adaptados a Ti
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Soluciones específicas para cada tipo de cliente
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {targetServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="nubia-shadow border-0 bg-white">
                  <CardHeader>
                    <div className="w-16 h-16 bg-nubia-green-support/10 rounded-2xl flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-nubia-green-support" />
                    </div>
                    <CardTitle className="text-3xl text-nubia-green-primary">{service.title}</CardTitle>
                    <CardDescription className="text-lg pt-2">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-nubia-gold-primary mt-0.5 flex-shrink-0" />
                          <span className="text-nubia-text-dark/70">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para Empezar?
          </h2>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-nubia-white/90">
            Solicita tu presupuesto personalizado sin compromiso
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Solicitar Presupuesto
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculadora-solar">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nubia-green-primary px-10 py-7 text-lg">
                Calcular Mi Ahorro
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servicios;